// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { Observable, of, timer } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import {
  ApplicationError,
  EmailAddressValidationModel,
  ErrorMessage,
  LanguageModel,
  PasswordResetResponseModel,
  PatchModel,
  PatchOperations,
  SettingsModel,
  ToastModel,
  UserDetailsModel,
  UserProfileModel,
} from '../../../models';
import { environment } from '../../../../environments/environment';
import { AppService } from '../app.service';
import { AppToastService } from '../app-toast.service';
import { ProfileBaseService } from './profile-base.service';
import { User } from 'oidc-client';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class ProfileService extends ProfileBaseService {
  constructor(
    private http: HttpClient,
    protected appService: AppService,
    private translate: TranslateService,
    private toastService: AppToastService
  ) {
    super();
  }

  loadProfile(): void {
    if (this._isLoaded) {
      return;
    }

    let url = environment.apiConfig.surveyApiUrl + environment.apiConfig.profileApi;

    this.http
      .get<UserDetailsModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe((result: UserDetailsModel | null) => {
        this._userDetails$.next(result);
      });

    url = environment.apiConfig.surveyApiUrl + environment.apiConfig.settingsApi;

    this.http
      .get<SettingsModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._settingsError.bind(this))
      )
      .subscribe(settings => {
        this._settings$.next(settings);
      });

    this._isLoaded = true;
  }

  update(userProfile: UserProfileModel): Observable<boolean> {
    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.profileApi;

    return this.http
      .put<boolean>(url, { userDetails: userProfile })
      .pipe(
        retryBackoff(environment.retryConfig),
        tap(result => this._userDetails$.next(null)),
        catchError(this._updateError.bind(this))
      );
  }

  refreshEmailAddress(email: string) {
    if (!this._userDetails$?.value) return;

    this._userDetails$.value.emailAddress = email;
  }

  updateUserDetails(userDetails: UserDetailsModel): void {
    if (!userDetails) {
      return;
    }

    if (this._userDetails$.value) {
      userDetails.emailAddress = this._userDetails$.value.emailAddress;
    }

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.profileApi;
    this._isUpdating$.next(true);
    this.http
      .put<UserDetailsModel>(url, userDetails)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe(ud => {
        if (ud) {
          if (ud.hasUpdateError) {
            this._updateUserDetailsError$.next(ud.updateErrorMessage);
          } else {
            this.toastService.show({
              header: this.translate.instant('updateSuccessHeader'),
              body: this.translate.instant('updateUserDetailsSuccessBody'),
              class: 'bg-success text-light'
            } as ToastModel);
          }
          this._userDetails$.next(ud);
          this._updateUserDetailsResult$.next(!ud.hasUpdateError);
          this._isUpdating$.next(false);
        } else {
          this._updateUserDetailsError$.next(this.translate.instant('errorMessageGenericBackend'));
        }
      });
  }

  updateLoginDetails(loginDetails: UserDetailsModel): void {
    if (!loginDetails) {
      return;
    }

    if (this._userDetails$.value) {
      loginDetails.firstName = this._userDetails$.value.firstName;
      loginDetails.lastName = this._userDetails$.value.lastName;
    }

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.profileApi;
    this._isUpdating$.next(true);
    this.http
      .put<UserDetailsModel>(url, loginDetails)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe(ud => {
        if (ud) {
          if (ud.hasUpdateError) {
            this._updateLoginDetailsError$.next(ud.updateErrorMessage);
          } else {
            this.toastService.show({
              header: this.translate.instant('updateSuccessHeader'),
              body: this.translate.instant('updateLoginDetailsSuccessBody'),
              class: 'bg-success text-light'
            } as ToastModel);
          }
          this._userDetails$.next(ud);
          this._updateLoginDetailsResult$.next(!ud.hasUpdateError);
          this._isUpdating$.next(false);
        } else {
          this._updateLoginDetailsError$.next(this.translate.instant('errorMessageGenericBackend'));
        }
      });
  }

  requestPasswordChange(): void {
    const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.requestPasswordResetApi;
    this._isUpdating$.next(true);
    this.http.post<PasswordResetResponseModel>(url, null)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._resetPasswordError.bind(this))
      ).subscribe(rpr => {
        this._isUpdating$.next(false);
        if (rpr && !rpr.hasError) {
          this.toastService.show({
            header: this.translate.instant('forgotPasswordConfirmationTitle'),
            body: this.translate.instant('forgotPasswordConfirmationText'),
            class: 'bg-success text-light'
          } as ToastModel);
        }
      });
  }

  checkIfEmailAddressInUse(emailAddress: string): void {
    if (!emailAddress) {
      return;
    }
    const uri =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.checkEmailAddressApi +
      `?emailAddress=${encodeURIComponent(emailAddress)}`;
    this._isValidatingEmailAddress$.next(true);

    this.http
      .get<EmailAddressValidationModel>(uri)
      .pipe(catchError(this._emailError.bind(this)))
      .subscribe((result: EmailAddressValidationModel | null) => {
        this._isEmailAddressInUse$.next(result);
        this._isValidatingEmailAddress$.next(false);
      });
  }

  updateAvatar(image: string) {
    if (!image) {
      return;
    }

    const currentUserDetails = this._userDetails$.value;
    this._updatingAvatar$.next(true);

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.profileApi;

    const model = {
      operation: PatchOperations.replace,
      path: 'avatarpic',
      value: image
    } as PatchModel;

    this.http.patch(url, model).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._updateAvatarError.bind(this))
    ).subscribe(() => {
      this._updatingAvatar$.next(false);
      this._avatarUpdated$.next(true);
      if (currentUserDetails) {
        this._userDetails$.next({...currentUserDetails, avatar: image});
      }
    });
  }

  removeAvatar() {
    const currentUserDetails = this._userDetails$.value;

    const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.profileApi;

    const model = {
      operation: PatchOperations.remove,
      path: 'avatarpic',
      value: ''
    } as PatchModel;

    this._removingAvatar$.next(true);

    this.http.patch(url, model).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._updateAvatarError.bind(this))
    ).subscribe(() => {
      this._removingAvatar$.next(false);
      this._avatarRemoved$.next(true);
      if (currentUserDetails) {
        this._userDetails$.next({...currentUserDetails, avatar: ''} as UserDetailsModel);
      }
    });
  }

  private _modelError(err: any, caught: any): Observable<UserDetailsModel | null> {
    // For mobile treat as a fatal error

    this._handleError(err);
    return of(null);
  }

  private _settingsError(err: any, caught: any): Observable<SettingsModel | null> {
    this._handleError(err);
    return of(null);
  }

  private _updateError(err: any, caught: any): Observable<boolean> {
    this._handleError(err);
    return of(false);
  }

  private _updateAvatarError(err: any, caught: any) {
    this._handleError(err);
    return of();
  }

  private _emailError(err : any, caught : any): Observable<EmailAddressValidationModel | null> {
    this._isValidatingEmailAddress$.next(false);
    this._handleError(err);

    return of(null);
  }

  private _resetPasswordError(err: any, caught: any): Observable<PasswordResetResponseModel> {
    this._handleError(err);
    return of({ hasError: true} as PasswordResetResponseModel);
  }

  private _handleError(err: any) {
    this.appService.setLoading(false);
    this._isUpdating$.next(false);

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'ProfileService',
      true,
      err
    );

    this.appService.setLastError(error);
  }
}
